import { useCallback, useState, type ComponentType, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { UseMutationResult } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import {
  Button,
  DataTable,
  DeleteConfirmDialog,
  Page,
  PageContent,
  PageHeader,
  type DataTableColumnDef
} from '@bliks-io/ui';
import { ContentHeader } from '@/components/content-header';
import type { AppIcon } from '@/constants/entityIcons';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import type { ApiError } from '@/lib/agent.types';
import { translateErrorMessage } from '@/lib/errors';
import { toastRequestError } from '@/api/mutations/toast-api-error';
import { logger } from '@/lib/logger';

const NO_ROWS: never[] = [];

export interface EntityListMutations<TCreateData, TUpdateData, TCreateResult> {
  /** Omitted when the page cannot create — the create button then never renders. */
  create?: UseMutationResult<TCreateResult, Error, TCreateData>;
  update?: UseMutationResult<unknown, Error, { id: string; data: TUpdateData }>;
  /** Omitted when the page cannot delete — the confirm dialog then never renders. */
  remove?: UseMutationResult<unknown, Error, string>;
}

export interface EntityListColumnArgs<TRow> {
  rows: TRow[];
  openEdit: (row: TRow) => void;
  openDelete: (id: string) => void;
}

export interface EntityListFormDialogArgs<TRow, TCreateData, TUpdateData, TCreateResult> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Undefined in create mode. */
  entity: TRow | undefined;
  isSubmitting: boolean;
  /**
   * Resolves with the create mutation's result, so a dialog can chain follow-up
   * work on the new entity; an update resolves undefined. Rejections propagate:
   * FormDialog routes them onto the form and the dialog stays open.
   */
  onSubmit: (data: TCreateData | TUpdateData) => Promise<TCreateResult | undefined>;
}

export interface EntityListPageDescriptor<
  TRow extends { _id: string },
  TCreateData,
  TUpdateData,
  TCreateResult
> {
  documentTitleKey: string;
  /** Resolves `.title`, `.description`, `.empty`, `.create` and `.search`. */
  i18nPrefix: string;
  icon: AppIcon;
  TabsNav?: ComponentType;
  tableId: string;
  enableGlobalFilter?: boolean;
  /**
   * Wrap the query hook, never reference it (`() => useMySystems()`, not
   * `useMySystems`): a bare reference reads the export when this module is
   * imported, which breaks every suite that partially mocks the query module.
   * The same holds for `useMutations` and `useColumns`.
   */
  useRows: () => { data: TRow[] | undefined; isLoading: boolean };
  useMutations: () => EntityListMutations<TCreateData, TUpdateData, TCreateResult>;
  useColumns: (args: EntityListColumnArgs<TRow>) => DataTableColumnDef<TRow>[];
  renderFormDialog: (
    args: EntityListFormDialogArgs<TRow, TCreateData, TUpdateData, TCreateResult>
  ) => ReactNode;
  /** Rendered left of the create button — the one slot for page-specific header actions. */
  renderHeaderActions?: () => ReactNode;
  deleteEntityPhraseKey: string;
}

/**
 * Builds a CRUD list page from a descriptor: dialog state, submit dispatch,
 * delete confirmation and the page chrome live here, everything page-specific
 * (query, mutations, columns, form dialog) comes from the descriptor.
 *
 * Call this at module scope. Calling it during a render hands React a new
 * component identity every time, which remounts the whole page and drops its
 * dialog state.
 *
 * A page that does not fit this shape stays hand-written.
 */
export function createEntityListPage<
  TRow extends { _id: string },
  TCreateData,
  TUpdateData,
  TCreateResult
>(descriptor: EntityListPageDescriptor<TRow, TCreateData, TUpdateData, TCreateResult>) {
  const {
    documentTitleKey,
    i18nPrefix,
    icon,
    TabsNav,
    tableId,
    enableGlobalFilter,
    useRows,
    useMutations,
    useColumns,
    renderFormDialog,
    renderHeaderActions,
    deleteEntityPhraseKey
  } = descriptor;

  return function EntityListPage() {
    const { t } = useTranslation();
    useDocumentTitle(documentTitleKey);

    const { data, isLoading } = useRows();
    const { create, update, remove } = useMutations();

    const [formDialogOpen, setFormDialogOpen] = useState(false);
    const [editingEntity, setEditingEntity] = useState<TRow | undefined>();
    const [deletingEntityId, setDeletingEntityId] = useState<string | null>(null);

    const openCreate = useCallback(() => {
      setEditingEntity(undefined);
      setFormDialogOpen(true);
    }, []);

    const openEdit = useCallback((row: TRow) => {
      setEditingEntity(row);
      setFormDialogOpen(true);
    }, []);

    const openDelete = useCallback((id: string) => setDeletingEntityId(id), []);

    const closeFormDialog = useCallback(() => {
      setFormDialogOpen(false);
      setEditingEntity(undefined);
    }, []);

    const rows = data ?? NO_ROWS;
    const columns = useColumns({ rows, openEdit, openDelete });

    const handleSubmit = useCallback(
      async (values: TCreateData | TUpdateData) => {
        // Rejecting beats closing on a descriptor that opens an affordance it
        // has no mutation for: the dialog stays open with the edits intact.
        if (editingEntity) {
          if (!update) {
            throw new Error(`EntityListPage (${i18nPrefix}) has no update mutation`);
          }
          await update.mutateAsync({ id: editingEntity._id, data: values as TUpdateData });
          closeFormDialog();
          return undefined;
        }
        if (!create) {
          throw new Error(`EntityListPage (${i18nPrefix}) has no create mutation`);
        }
        const created = await create.mutateAsync(values as TCreateData);
        closeFormDialog();
        return created;
      },
      [editingEntity, update, create, closeFormDialog]
    );

    const handleDeleteConfirm = useCallback(async () => {
      if (!deletingEntityId || !remove) return;
      try {
        await remove.mutateAsync(deletingEntityId);
        setDeletingEntityId(null);
      } catch (error) {
        logger.error('Failed to delete entity:', error);
        toastRequestError(error, translateErrorMessage(error as ApiError, t));
      }
    }, [deletingEntityId, remove, t]);

    return (
      <Page>
        {TabsNav && (
          <PageHeader>
            <TabsNav />
          </PageHeader>
        )}
        <PageContent>
          <div className="flex items-center justify-between">
            <ContentHeader
              icon={icon}
              title={t(`${i18nPrefix}.title`)}
              description={t(`${i18nPrefix}.description`)}
            />
            <div className="flex items-center gap-3">
              {renderHeaderActions?.()}
              {create && (
                <Button variant="primary" size="medium" onClick={openCreate}>
                  <Plus className="h-4 w-4" />
                  {t(`${i18nPrefix}.create`)}
                </Button>
              )}
            </div>
          </div>

          <div className="mt-8">
            <DataTable
              columns={columns}
              data={rows}
              isLoading={isLoading}
              emptyMessage={t(`${i18nPrefix}.empty`)}
              enableGlobalFilter={enableGlobalFilter}
              globalSearchPlaceholder={enableGlobalFilter ? t(`${i18nPrefix}.search`) : undefined}
              enableColumnVisibility
              enableColumnFilters
              enableUrlState
              tableId={tableId}
            />
          </div>

          {renderFormDialog({
            open: formDialogOpen,
            onOpenChange: (open) => !open && closeFormDialog(),
            entity: editingEntity,
            isSubmitting: (create?.isPending ?? false) || (update?.isPending ?? false),
            onSubmit: handleSubmit
          })}

          {remove && (
            <DeleteConfirmDialog
              open={deletingEntityId !== null}
              onOpenChange={(open) => !open && setDeletingEntityId(null)}
              onConfirm={handleDeleteConfirm}
              isDeleting={remove.isPending}
              entityPhrase={t(deleteEntityPhraseKey)}
            />
          )}
        </PageContent>
      </Page>
    );
  };
}
